import React, { useContext, useEffect, useRef, useState } from "react";
import { Alert, Button, Dropdown, Form, Modal, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FiBell, FiMoon, FiSun } from "react-icons/fi";
import { FaCrown } from "react-icons/fa";
import api, { BASE_URL } from "../../Utils/api";
import { GlobalContext } from "../../Utils/GlobalContext";
import { getPermFlags } from "../../Utils/controlaPermissao";
import { getVersaoApp } from "../../Utils/versaoApp";
import NovidadesVersaoModal from "../Login/NovidadesVersaoModal";
import LoginSelecaoEntidade from "../Login/LoginSelecaoEntidade";
import EditorRicoAnexos from "../Contato/EditorRicoAnexos";
import MensagemBalao from "../Contato/MensagemBalao";
import "./UserSettingsMenu.css";

const FALLBACK_VERSION = (() => {
  const v = getVersaoApp();
  return v !== "—" ? v : "1.0";
})();

function UserSettingsMenu() {
  const navigate = useNavigate();
  const { globalData, setGlobalData, darkMode, toggleDarkMode, menuType, setMenuType } = useContext(GlobalContext);
  const { constPermiteEntidades } = getPermFlags(globalData);
  const [versao, setVersao] = useState(null);
  const [ultimaVersaoLocal, setUltimaVersaoLocal] = useState(null);
  const [showNovidadesModal, setShowNovidadesModal] = useState(false);
  const [showContatoModal, setShowContatoModal] = useState(false);
  const [contatoAlertas, setContatoAlertas] = useState([]);
  const [contatoConversa, setContatoConversa] = useState(null);
  const [contatoMensagens, setContatoMensagens] = useState([]);
  const [enviandoContatoResposta, setEnviandoContatoResposta] = useState(false);
  const contatoEditorRef = useRef(null);
  const [dropdownMenuWidth, setDropdownMenuWidth] = useState(null);
  const [showTrocarEntidade, setShowTrocarEntidade] = useState(false);
  const [entidadesParaTrocar, setEntidadesParaTrocar] = useState([]);
  const [podeTrocarEntidade, setPodeTrocarEntidade] = useState(false);
  const [trocandoEntidade, setTrocandoEntidade] = useState(false);
  const userMenuToggleRef = useRef(null);

  const userName =
    globalData?.user || localStorage.getItem("user") || "Usuário";
  const isSuperAdmin =
    (globalData?.super_admin || localStorage.getItem("super_admin")) === "S";
  const temNovidades = Boolean(
    globalData?.temNovidades &&
      Array.isArray(globalData?.novidadesVersao) &&
      globalData.novidadesVersao.length > 0
  );
  const novidadesVersao = globalData?.novidadesVersao || [];
  const userId = globalData?.user_id || localStorage.getItem("user_id");

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) return;
    api
      .get(`${BASE_URL}/api/versoes/ultima`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const v = res.data?.ultimaVersao;
        if (v && v.versao != null) {
          setVersao(v.versao);
          setUltimaVersaoLocal({
            id: v.id,
            versao: v.versao,
            data_liberacao: v.data_liberacao,
            titulo: v.titulo || null,
            conteudo: v.conteudo || null,
          });
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token || !userId) {
      setPodeTrocarEntidade(false);
      setEntidadesParaTrocar([]);
      return;
    }

    api
      .get(`${BASE_URL}/api/auth/entidades_usuario/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const lista = res.data?.entidades || [];
        setEntidadesParaTrocar(lista);
        setPodeTrocarEntidade(lista.length > 1);
      })
      .catch(() => {
        setPodeTrocarEntidade(false);
        setEntidadesParaTrocar([]);
      });
  }, [userId]);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) return;
    api
      .get("/api/alertas")
      .then((res) => {
        const itens = res.data?.mensagensContato?.itens;
        setContatoAlertas(Array.isArray(itens) ? itens : []);
      })
      .catch(() => {
        setContatoAlertas([]);
      });
  }, [userId]);

  const handleLogout = async () => {
    try {
      await api.post(`${BASE_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch {
      // ignora erro no logout
    } finally {
      localStorage.removeItem("authToken");
      localStorage.removeItem("sidebarCollapsed");
      localStorage.removeItem("entidade_id");
      localStorage.removeItem("selectedEntidade");
      localStorage.removeItem("user");
      localStorage.removeItem("user_id");
      localStorage.removeItem("permissoes_grupo");
      localStorage.removeItem("classificacao");
      localStorage.removeItem("numero_linhas_grid");
      localStorage.removeItem("temNovidades");
      localStorage.removeItem("novidadesVersao");

      setGlobalData({
        user_id: null,
        entidade_id: null,
        user: null,
        permissoes_grupo: null,
        classificacao: null,
        numero_linhas_grid: 10,
        logotipo: null,
        rota_padrao: null,
        pagina_padrao_classificacao: null,
        temNovidades: false,
        novidadesVersao: [],
      });

      window.location.replace(`${window.location.origin}/login`);
    }
  };

  const label =
    userName && userName.length > 30
      ? `${userName.substring(0, 27)}...`
      : userName;
  const initial = (userName && userName.trim()[0]) || "U";
  const currentVersion = versao ?? FALLBACK_VERSION;

  const handleOpenNovidades = async () => {
    if (!ultimaVersaoLocal && !temNovidades) {
      const token = localStorage.getItem("authToken");
      try {
        const res = await api.get(`${BASE_URL}/api/versoes/ultima`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const v = res.data?.ultimaVersao;
        if (v?.versao != null) {
          setUltimaVersaoLocal({
            id: v.id,
            versao: v.versao,
            data_liberacao: v.data_liberacao,
            titulo: v.titulo || null,
            conteudo: v.conteudo || null,
          });
        }
      } catch {}
    }
    setShowNovidadesModal(true);
  };

  // Indica se há algum alerta não lido. Para novos tipos: incluir aqui, ex.: temAlertas = temNovidades || temSolicitacoesPendentes;
  const abrirConversaContato = async (idConversa) => {
    const { data } = await api.get(`/api/contato/conversas/${idConversa}`);
    setContatoConversa(data.conversa);
    setContatoMensagens(data.mensagens || []);
    if (contatoEditorRef.current) contatoEditorRef.current.limpar();
    setShowContatoModal(true);
    setContatoAlertas((atuais) => atuais.filter((item) => Number(item.id) !== Number(idConversa)));
  };

  const handleOpenAlertas = async () => {
    if (contatoAlertas.length > 0) {
      try {
        await abrirConversaContato(contatoAlertas[0].id);
        return;
      } catch (err) {
        console.error("Erro ao abrir alerta de contato:", err);
      }
    }
    handleOpenNovidades();
  };

  const responderContato = async () => {
    const conteudo = contatoEditorRef.current ? contatoEditorRef.current.obterConteudo() : null;
    if (!contatoConversa || !conteudo || (!conteudo.mensagem_texto.trim() && conteudo.anexos.length === 0)) return;
    setEnviandoContatoResposta(true);
    try {
      await api.post(`/api/contato/conversas/${contatoConversa.id}/responder`, {
        mensagem_html: conteudo.mensagem_html,
        mensagem_texto: conteudo.mensagem_texto,
        anexos: conteudo.anexos,
      });
      await abrirConversaContato(contatoConversa.id);
    } catch (err) {
      console.error("Erro ao responder contato:", err);
    } finally {
      setEnviandoContatoResposta(false);
    }
  };

  const temAlertas = temNovidades || contatoAlertas.length > 0;

  const handleEntidade = () => {
    setGlobalData((prev) => ({ ...prev, classificacao: "E" }));
    localStorage.setItem("classificacao", "E");
    navigate("/Entidades");
  };

  const handleAbrirTrocarEntidade = async () => {
    if (entidadesParaTrocar.length > 1) {
      setShowTrocarEntidade(true);
      return;
    }

    const token = localStorage.getItem("authToken");
    if (!token || !userId) return;
    try {
      const res = await api.get(`${BASE_URL}/api/auth/entidades_usuario/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const lista = res.data?.entidades || [];
      setEntidadesParaTrocar(lista);
      setPodeTrocarEntidade(lista.length > 1);
      if (lista.length > 1) {
        setShowTrocarEntidade(true);
      }
    } catch (err) {
      console.error("Erro ao buscar entidades para troca:", err);
    }
  };

  const handleEntidadeTrocada = async (entidadeId) => {
    setShowTrocarEntidade(false);
    setTrocandoEntidade(true);

    // Atualiza globalData ANTES de modificar o localStorage.
    // O GlobalContext persiste entidade_id via useEffect([globalData.entidade_id]):
    // se não atualizarmos o globalData aqui, qualquer setGlobalData que disparar
    // durante os 3s (fetchConfig, fetchPermissoes, etc.) sobrescreve o localStorage
    // com a entidade antiga, e o reload carrega a entidade errada.
    setGlobalData((prev) => ({
      ...prev,
      entidade_id: String(entidadeId),
      classificacao: null,
      permissoes_grupo: null,
    }));

    localStorage.setItem("entidade_id", String(entidadeId));
    localStorage.setItem("selectedEntidade", String(entidadeId));
    localStorage.removeItem("classificacao");
    localStorage.removeItem("permissoes_grupo");

    await new Promise((resolve) => setTimeout(resolve, 3000));

    window.location.replace(window.location.origin + "/");
  };

  const handleCloseNovidadesModal = async () => {
    if (temNovidades) {
      const ids = (novidadesVersao || [])
        .map((n) => n.id)
        .filter((id) => id != null);
      if (ids.length > 0) {
        try {
          const token = localStorage.getItem("authToken");
          await api.post(
            `${BASE_URL}/api/versoes/visualizar`,
            { id_versoes: ids },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (err) {
          console.error("Erro ao marcar novidades como visualizadas:", err);
        }
      }
      setGlobalData((prev) => ({
        ...prev,
        temNovidades: false,
        novidadesVersao: [],
      }));
    }
    setShowNovidadesModal(false);
  };

  const listaModal = temNovidades
    ? novidadesVersao
    : (ultimaVersaoLocal ? [ultimaVersaoLocal] : []);

  return (
    <div className="user-settings-header">
      <OverlayTrigger
        placement="bottom"
        overlay={
          <Tooltip id="header-alertas-tooltip">
            {temAlertas ? "Alertas não lidos - Clique para ver" : "Alertas"}
          </Tooltip>
        }
      >
        <button
          className="alertas-bell-btn"
          onClick={handleOpenAlertas}
          aria-label="Ver alertas"
        >
          <FiBell />
          {temAlertas && <span className="alertas-bell-badge" aria-hidden />}
        </button>
      </OverlayTrigger>

      <OverlayTrigger
        placement="bottom"
        overlay={<Tooltip id="dark-mode-tooltip">{darkMode ? "Modo claro" : "Modo escuro"}</Tooltip>}
      >
        <button
          className="dark-mode-toggle-btn"
          onClick={toggleDarkMode}
          aria-label={darkMode ? "Ativar modo claro" : "Ativar modo escuro"}
        >
          {darkMode ? <FiSun /> : <FiMoon />}
        </button>
      </OverlayTrigger>

      <Dropdown
        align="end"
        onToggle={(isOpen) => {
          if (isOpen && userMenuToggleRef.current) {
            setDropdownMenuWidth(userMenuToggleRef.current.offsetWidth);
          }
        }}
      >
        <Dropdown.Toggle
          ref={userMenuToggleRef}
          variant=""
          id="user-settings-dropdown"
          className="user-menu-toggle"
        >
          <span className="user-avatar">{initial.toUpperCase()}</span>
          <span className="user-name-text d-none d-sm-inline">{label}</span>
        </Dropdown.Toggle>

        <Dropdown.Menu
          className="user-settings-dropdown-menu"
          style={dropdownMenuWidth != null ? { "--user-toggle-width": `${dropdownMenuWidth}px` } : undefined}
        >
          {constPermiteEntidades && (
            <Dropdown.Item onClick={handleEntidade}>Entidade</Dropdown.Item>
          )}
          {podeTrocarEntidade && (
            <Dropdown.Item onClick={handleAbrirTrocarEntidade}>Trocar Entidade</Dropdown.Item>
          )}
          <Dropdown.Item onClick={handleOpenNovidades}>
            Versão {currentVersion != null ? `(v${currentVersion})` : ""}
          </Dropdown.Item>

          <Dropdown.Divider />
          <div className="px-3 pb-1">
            <small className="text-muted d-block mb-1" style={{ fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.04em" }}>
              MENU
            </small>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                id="menu-lateral"
                label="Lateral"
                name="menuType"
                value="lateral"
                checked={menuType === "lateral"}
                onChange={() => setMenuType("lateral")}
              />
              <Form.Check
                type="radio"
                id="menu-superior"
                label="Superior"
                name="menuType"
                value="superior"
                checked={menuType === "superior"}
                onChange={() => setMenuType("superior")}
              />
            </div>
          </div>

          {isSuperAdmin && (
            <>
              <Dropdown.Divider />
              <Dropdown.Item
                onClick={() => navigate("/GodMode")}
                style={{ color: "#7c3aed", fontWeight: 600 }}
              >
                <FaCrown size={13} className="me-2" style={{ color: "#f59e0b" }} />
                GOD Mode
              </Dropdown.Item>
            </>
          )}
          <Dropdown.Divider />
          <Dropdown.Item onClick={handleLogout}>Sair</Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>

      <NovidadesVersaoModal
        show={showNovidadesModal}
        novidadesVersao={listaModal}
        onClose={handleCloseNovidadesModal}
      />

      <Modal show={showContatoModal} onHide={() => setShowContatoModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{contatoConversa ? `Mensagem #${contatoConversa.id}` : "Mensagem"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {contatoConversa && (
            <>
              <Alert variant="info" className="py-2">
                {contatoConversa.tipo_label} - {contatoConversa.titulo}
              </Alert>
              <div className="alertas-contato-thread">
                {contatoMensagens.map((mensagem) => (
                  <MensagemBalao
                    key={mensagem.id}
                    mensagem={mensagem}
                    autor={mensagem.origem === "GODMODE" ? "Duxen" : "Voce"}
                    titulo={`Mensagem de ${mensagem.origem === "GODMODE" ? "Duxen" : "Voce"}`}
                    className={`alertas-contato-msg ${mensagem.origem === "GODMODE" ? "duxen" : "usuario"}`}
                  />
                ))}
              </div>

              {contatoConversa.status !== "ENCERRADA" ? (
                <Form.Group className="mt-3">
                  <Form.Label>Replica</Form.Label>
                  <EditorRicoAnexos
                    ref={contatoEditorRef}
                    placeholder="Escreva sua resposta para a Duxen..."
                    disabled={enviandoContatoResposta}
                  />
                </Form.Group>
              ) : (
                <Alert variant="secondary" className="mt-3 mb-0">Esta conversa foi encerrada.</Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowContatoModal(false)}>Fechar</Button>
          {contatoConversa?.status !== "ENCERRADA" && (
            <Button onClick={responderContato} disabled={enviandoContatoResposta}>
              {enviandoContatoResposta ? <Spinner animation="border" size="sm" /> : null}
              Enviar replica
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <LoginSelecaoEntidade
        show={showTrocarEntidade}
        entidades={entidadesParaTrocar}
        handleSelect={handleEntidadeTrocada}
        onCancel={() => setShowTrocarEntidade(false)}
        autoSelect={false}
      />

      {trocandoEntidade && (
        <div style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
        }}>
          <Spinner animation="border" variant="light" style={{ width: "3rem", height: "3rem" }} />
          <p style={{ color: "#fff", marginTop: "1.25rem", fontSize: "1rem", fontWeight: 500 }}>
            Trocando entidade...
          </p>
        </div>
      )}
    </div>
  );
}

export default UserSettingsMenu;
